import { useRef, useEffect, useState, useReducer } from "react";
import Satellite from "./components/Satellite";
import styled from "styled-components";
import mapboxGl from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import Contacts2 from "./Contacts2";
import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import useConditionalEffect from "./hooks/useConditionalEffect";
import { switchLayerReduce } from "./reducers/switchLayerReduce";
import SearchBar from "./components/SearchBar";
import SelectLayer from "./components/LayerSelect/SelectLayer";
import DrawToolFirstMode from "./components/DrawTool/DrawToolFirstMode";
import SwitchMode from "./components/SwitchMode";
import CalculatedInitialAsessmentParams from "./components/CalculatedResults/CalculatedInitialAsessmentParams";
import { drawStyles } from "./constants/drawStyles";
import { clearBufferLayers } from "./utils/bufferUtils";
import { add3DBuildingsLayer, addEffisWms, addFirePolygons, addLandcoverLayer, addSatelliteLayer } from "./utils/layerUtils";
import { clearAllHandlers, clearLayers, registerDrawHandler } from "./utils/handlersUtils";
import ZoomControl from "./components/Controls/ZoomControl";
import ResetHorth from "./components/Controls/ResetHorth";
import CalculatedFireFenceParams from "./components/CalculatedResults/CalculatedFireFenceParams";
import CalculatedAnalysedParams from "./components/CalculatedResults/CalculatedAnalysedParams";
import { clearAssetPaintOverlay } from "./utils/assetRiskUtils";
import { purgeMeasurementLayers } from "./utils/measurementUtils";
import Error from "./components/Error";

const App = () => {
  const [whichMode, setWichMode] = useState("area");
  const [drawMode, setDrawMode] = useState({
    area: "simple_select",
    home: "simple_select",
    point: "simple_select",
  });
  const mapRef = useRef(null);
  const toggleDrawModeRef = useRef(null);
  const [layers, setLayers] = useReducer(switchLayerReduce, { isFireOn: false, isEffisOn: false, isSatellite: false });
  const [isFirstMode, setIsFirstMode] = useState(true);
  const [mapInit, setMapInit] = useState(false);
  const [fireFenceParams, setFireFenceParams] = useState({
    isOpen: false,
  });
  const [initialAsessmentParams, setInitialAsessmentParams] = useState({
    isOpen: false,
  });
  const [analysedParams, setAnalysedParams] = useState({
    isOpen: false,
  });

  useEffect(() => {
    mapboxGl.accessToken = "pk.eyJ1IjoidG9wb2wiLCJhIjoiY2lndjM4eDNxMDA0M3Zma3JiOGRmcGNyOSJ9.tPBrXFyMAspRCTjyVKmx8A";

    const map = new mapboxGl.Map({
      container: mapRef.current || "",
      style: `mapbox://styles/mapbox/light-v10`,
      center: [23.529443117620076, 38.39412366730738],
      zoom: 13.921309945861651,
      interactive: true,
      maxTileCacheSize: 100000,
      refreshExpiredTiles: true,
      preserveDrawingBuffer: true,
    });

    const draw = new MapboxDraw({
      displayControlsDefault: false,
      styles: drawStyles,
    });

    setMapInit(true);

    const scale = new mapboxGl.ScaleControl();

    map.addControl(scale);
    map.addControl(draw);

    const scaleContainer = scale._container;

    scaleContainer.parentNode.removeChild(scaleContainer);
    map.getContainer().appendChild(scaleContainer);
    scaleContainer.style.position = "absolute";
    scaleContainer.style.bottom = "20px";
    scaleContainer.style.right = "20px";

    window.map = map;
    window.draw = draw;

    toggleDrawModeRef.current = (drawMode) => {
      draw.changeMode(draw.getMode() === "simple_select" ? drawMode : "simple_select");
    };

    map.on("style.load", async () => {
      addLandcoverLayer(map);
      addSatelliteLayer(map);
      await addFirePolygons(map);
      addEffisWms(map);
      add3DBuildingsLayer(map);
    });
  }, []);

  useEffect(() => {
    const map = window.map;
    if (!map) return;

    const setVisibilityIfExists = (layerId, visibility) => {
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, "visibility", visibility);
      }
    };

    if (layers.isSatellite) {
      setVisibilityIfExists("satellite-layer", "visible");
      setVisibilityIfExists("3d-buildings", "visible");
      setVisibilityIfExists("building-hit", "visible");
    } else {
      setVisibilityIfExists("satellite-layer", "none");
      setVisibilityIfExists("3d-buildings", "none");
      setVisibilityIfExists("building-hit", "none");
    }

    if (layers.isFireOn) {
      setVisibilityIfExists("fire-polygons-fill", "visible");
      setVisibilityIfExists("fire-polygons-outline", "visible");
    } else {
      setVisibilityIfExists("fire-polygons-fill", "none");
      setVisibilityIfExists("fire-polygons-outline", "none");
    }

    if (layers.isEffisOn) {
      setVisibilityIfExists("effis-wms-layer", "visible");
    } else {
      setVisibilityIfExists("effis-wms-layer", "none");
    }
  }, [layers]);

  useConditionalEffect(
    () => registerDrawHandler(window.map, window.draw, setFireFenceParams, setInitialAsessmentParams, setAnalysedParams, setDrawMode, whichMode),
    [drawMode[whichMode], whichMode]
  );

  const toggleDrawMode = () => {
    setWichMode("area");
    setDrawMode((prev) => ({
      ...prev,
      area: prev.area === "simple_select" ? "draw_line_string" : "simple_select",
    }));
    if (toggleDrawModeRef.current) {
      toggleDrawModeRef.current("draw_line_string");
    }
  };

  const toggleMode = (isFirst) => {
    setIsFirstMode(isFirst);
    handleClearAll();
  };

  const handleOpenFireFenceParams = (isOpen) => {
    if (!fireFenceParams?.perimeterLength) return;

    setFireFenceParams((prev) => ({
      ...prev,
      isOpen,
    }));
  }

  const handleInitialAsessmentParams = (isOpen) => {
    if (!initialAsessmentParams?.perimeterLength || !initialAsessmentParams?.maxAssetRisk) return;

    setInitialAsessmentParams((prev) => ({
      ...prev,
      isOpen,
    }));
  }

  const handleAnalysedParams = (isOpen) => {
    if (!analysedParams?.perimeterLength) return;

    setAnalysedParams((prev) => ({
      ...prev,
      isOpen,
    }));
  }

  const handleClearAll = () => {
    const map = window.map;
    const draw = window.draw;

    if (map.getLayer("line")) map.removeLayer("line");
    if (map.getLayer("line-outline")) map.removeLayer("line-outline");
    if (map.getSource("line")) map.removeSource("line");
    if (map.getSource("risk-driver-points")) {
      map.removeLayer("risk-driver-labels");
      map.removeSource("risk-driver-points");
    }
    if (map.getSource("nearest-asset-lot-line")) {
      map.removeLayer("nearest-asset-lot-line");
      map.removeLayer("nearest-asset-lot-line-label");
      map.removeSource("nearest-asset-lot-line");
    }

    clearAllHandlers(map);
    purgeMeasurementLayers(map);
    clearLayers();
    clearBufferLayers();
    clearAssetPaintOverlay(window.map);

    draw.deleteAll();
    draw.changeMode("simple_select");
    setAnalysedParams({ isOpen: false });
    setInitialAsessmentParams({ isOpen: false });
    setFireFenceParams({ isOpen: false });
  }

  return (
    <Error>
      <StyledMap>
        <div id="map-root" ref={mapRef} />
        {mapInit && (
          <>
            <SearchBar />
            <div className="panel">
              <SwitchMode toggleMode={toggleMode} mode={isFirstMode} />
              <SelectLayer
                isSatellite={layers.isSatellite}
                toggleSatellite={() => setLayers("TOGGLE_SATELLITE")}
                isFireDangerSHP={layers.isFireOn}
                toggleFireDangerSHP={() => setLayers("TOGGLE_FIRE")}
                isEffisFireDanger={layers.isEffisOn}
                toggleEffisFireDanger={() => setLayers("TOGGLE_EFFIS")}
              />
              {isFirstMode && <DrawToolFirstMode toggleDrawMode={toggleDrawMode} clear={handleClearAll} drawMode={drawMode} handleAnalysedParams={handleAnalysedParams} />}
              {!isFirstMode && (
                <Satellite
                  drawModeRef={toggleDrawModeRef}
                  drawMode={drawMode}
                  setDrawMode={setDrawMode}
                  whichMode={whichMode}
                  setWichMode={setWichMode}
                  isFirstMode={isFirstMode}
                  handleOpenFireFenceParams={handleOpenFireFenceParams}
                  handleInitialAsessmentParams={handleInitialAsessmentParams}
                  setFireFenceParams={setFireFenceParams}
                  setInitialAsessmentParams={setInitialAsessmentParams}
                  setAnalysedParams={setAnalysedParams}
                />
              )}
            </div>
            <CalculateWrapper>
              {initialAsessmentParams.isOpen && initialAsessmentParams.perimeterLength && initialAsessmentParams.maxAssetRisk && (
                <CalculatedInitialAsessmentParams {...initialAsessmentParams} handleInitialAsessmentParams={handleInitialAsessmentParams} />
              )}
              {analysedParams.isOpen && analysedParams.perimeterLength && <CalculatedAnalysedParams {...analysedParams} handleAnalysedParams={handleAnalysedParams} />}
              {fireFenceParams.isOpen && fireFenceParams.perimeterLength && (
                <CalculatedFireFenceParams {...fireFenceParams} handleOpenFireFenceParams={handleOpenFireFenceParams} />
              )}
            </CalculateWrapper>
            <ZoomControl />
            <ResetHorth />
          </>
        )}
        <Contacts2 />
      </StyledMap>
    </Error>
  );
};

const CalculateWrapper = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const StyledMap = styled.div`
  height: 100vh;
  width: 100vw;
  overflow: hidden;

  #map-root {
    width: 100%;
    height: 100%;
    flex-grow: 1;
  }

  .panel {
    position: absolute;
    top: 20px;
    left: 20px;
    width: 262px;
    display: grid;
    gap: 5px;
  }

  .mapboxgl-ctrl-bottom-right,
  .mapboxgl-ctrl-bottom-left {
    display: none;
  }
`;

export default App;


// const [lastFeatureCollection, setLastFeatureCollection] = useState({
//   type: "FeatureCollection",
//   features: [],
// });

// useEffect(() => {
//   if (isFirstMode) {
//     const riskDriverPoints = getRiskDriverPoints(window.draw);

//     setLastFeatureCollection((prev) => ({
//       ...prev,
//       features: [...riskDriverPoints],
//     }));

//     deleteRiskDriverPoints(window.map);
//     window.draw.deleteAll();
//   } else {
//     if (lastFeatureCollection.features && lastFeatureCollection.features.length) window.draw.add(lastFeatureCollection);
//     addRiskDriverPoints(window.map, lastFeatureCollection.features);
//   }
// }, [isFirstMode]);